import {
    Currency, Money, PaymentType,
    Stay, StayEntry,
} from 'widok-bridge';

const MAPPING = new Map([
    [`${Currency.ZLOTY}`, 'zł'],
    [`${Currency.EURO}`, '€'],
]);

export function formatPrice(price: Money | undefined,
    defaultToCurrency?: Currency): string {
    if (!price) { return '-'; }
    const res = Object.entries(price)
        .map(([key, value]) => (`${value.toFixed(2)} ${MAPPING.get(key)}`))
        .join(', ');
    if (res.length === 0 && defaultToCurrency !== undefined) {
        return `0 ${MAPPING.get(`${defaultToCurrency}`)}`;
    }
    return res;
}

export function formatPriceWithDefault(price: Money | undefined,
    defaultToCurrency?: Currency): string {
    return formatPrice(price, defaultToCurrency ?? Currency.ZLOTY);
}

/** Version of {@link formatPrice} that never returns '-'. */
export function formatMoney(price: Money | undefined,
    defaultToCurrency?: Currency): string {
    return formatPrice(price ?? {}, defaultToCurrency ?? Currency.ZLOTY);
}

export function simplifyMoney(money: Money | undefined) {
    const newMoney: Money = {};
    if (!money) return newMoney;
    for (const k in money) {
        const v = money[k] ?? 0;
        if (v !== 0) {
            newMoney[k] = (newMoney[k] ?? 0) + v;
        }
    }
    return newMoney;
}

/** Finds first difference in money and compares the different elements. */
export function compareMoney(moneyA: Money | undefined, moneyB: Money | undefined): number {
    const mA = simplifyMoney(moneyA ?? {});
    const mB = simplifyMoney(moneyB ?? {});
    for (const k in mA) {
        // Treat lack of currency as 0.
        let mBk = 0;
        if (mB.hasOwnProperty(k)) {
            mBk = mB[k];
        }
        if (mA[k] < mBk) return -1;
        if (mA[k] > mBk) return 1;
    }
    for (const k in mB) {
        // Treat lack of currency as 0.
        let mAk = 0;
        if (mA.hasOwnProperty(k)) {
            mAk = mA[k];
        }
        if (mAk < mB[k]) return -1;
        if (mAk > mB[k]) return 1;
    }
    return 0;
}

export function isEqualMoney(moneyA: Money | undefined, moneyB: Money | undefined): boolean {
    const mA = simplifyMoney(moneyA ?? {});
    const mB = simplifyMoney(moneyB ?? {});
    if (Object.keys(mA).length !== Object.keys(mB).length) {
        return false;
    }
    for (const k in mA) {
        if (!mB.hasOwnProperty(k)) return false;
        if (mB[k] !== mA[k]) return false;
    }
    return true;
}

export function addMoney(...money: (Money | undefined)[]): Money {
    const newMoney: Money = {};
    for (const m of money) {
        if (!m) continue;
        for (const k in m) {
            newMoney[k] = (newMoney[k] ?? 0) + m[k];
        }
    }
    return simplifyMoney(newMoney);
}

export function multiplyMoney(money: Money, factor: number): Money {
    const newMoney: Money = {};
    for (const k in money) {
        newMoney[k] = money[k] * factor;
    }
    return simplifyMoney(newMoney);
}

export function makeMoney(currency: number, amount: number): Money {
    if (currency !== Currency.EURO && currency !== Currency.ZLOTY) return {};
    const money: Money = {};
    money[currency] = amount;
    return simplifyMoney(money);
}

export function changeCurrency(money: Money, currency: number): Money {
    let amount: number | null = null;
    for (const k in money) {
        amount = money[k];
        break;
    }
    if (amount === null) { return {} };
    return makeMoney(currency, amount)
}

export function getEntryPrice(entry: StayEntry, settings?: { ignoreDiscount?: boolean }): Money {
    const prices: Money[] = [];
    if (entry.discount && !(settings?.ignoreDiscount ?? false)) {
        prices.push(multiplyMoney(makeMoney(entry.currency ?? 0, entry.discount), -1));
    }
    if (entry.items) {
        for (const key in entry.items) {
            const item = entry.items[key];
            if (item) prices.push(item.price ?? {});
        }
    }
    if (entry.reservationPrice) {
        prices.push(entry.reservationPrice);
    }
    return addMoney(...prices);
}

export function getStayTotalPrice(stay: Stay): Money {
    return addMoney(...(stay.entries?.map(e => getEntryPrice(e)) ?? []));
}

export function getStayPaidForPrice(stay: Stay): Money {
    return addMoney(...(stay.entries?.filter(e => e.isPaidFor)
        .map(e => getEntryPrice(e)) ?? []));
}

export function getStayTotalPaid(stay: Stay, type?: PaymentType): Money {
    return addMoney(...(stay.payments?.filter(p => (type === undefined || p.type === type))
        .map(p => p.money) ?? []));
}

function getCurrency(n: string | number): Currency {
    if (typeof n === 'string') n = Number.parseInt(n);
    if (n === Currency.ZLOTY) return Currency.ZLOTY;
    if (n === Currency.EURO) return Currency.EURO;
    return Currency.UNKNOWN;
}

export function getMoneyCurrency(money: Money | undefined, defaultValue = Currency.ZLOTY) {
    const arr = moneyToArray(money ?? {});
    if (arr.length === 0) {
        return defaultValue;
    }
    return arr[0].currency;
}

export function moneyToArray(money: Money) {
    return Object.keys(money)
        .map((k: string) => ({
            currency: getCurrency(k),
            amount: ((money as any)[k]) as number,
        }));
}